/**
 * The private / public / shared-with-me pill on a node row.
 *
 * Was declared INSIDE FoldersTable's render function, which makes it a new
 * component type on every render — React unmounts and remounts it each time,
 * throwing away DOM state and defeating memoisation. Hoisting it out is the
 * fix, not a stylistic preference.
 */

import { Globe, Lock, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

const BASE =
  'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-medium ' +
  'min-w-[80px] justify-center select-none';

export function VisibilityBadge({ visibility, className, shared = false }) {
  const { t } = useTranslation(['my-docs']);

  // For files reached through a share rather than owned, "Shared with me" is
  // the more meaningful label than the file's intrinsic visibility.
  if (shared) {
    return (
      <span className={cn(BASE, 'bg-purple-50 text-purple-600 border border-purple-100', className)}>
        <Users className="w-3 h-3" />
        <span className="truncate">{t('my-docs:visibility.shared') || 'Shared with me'}</span>
      </span>
    );
  }

  if (!visibility) return null;

  const isPublic = visibility === 'public';
  return (
    <span
      className={cn(
        BASE,
        isPublic
          ? 'bg-green-50 text-green-600 border border-green-100'
          : 'bg-gray-100 text-gray-600 border border-gray-200',
        className,
      )}
    >
      {isPublic ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
      <span className="truncate">
        {t(isPublic ? 'my-docs:visibility.public' : 'my-docs:visibility.private')}
      </span>
    </span>
  );
}
